import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { Building2, Check, Info } from 'lucide-react';

import BaseModal from '@/shared/components/modals/core/BaseModal.jsx';
import Input from '@/shared/components/elements/Input';
import Button from '@/shared/components/elements/Button';
import { tenantSchema } from '@/feature/Tenant/schemas/tenantSchemas';
import { extractFieldErrors } from '@/shared/utils/errorUtils';
import { cn } from '@/shared/utils/cn';

function TenantModal({
  isOpen,
  onClose,
  onSubmit,
  tenant = null,
  isLoading = false,
}) {
  const [serverErrors, setServerErrors] = useState({});
  const isEditMode = !!tenant;

  const form = useForm({
    defaultValues: {
      name: tenant?.name || '',
      description: tenant?.description || '',
    },
    validatorAdapter: zodValidator(),
    onSubmit: async ({ value }) => {
      try {
        setServerErrors({});
        await onSubmit(value);
        onClose();
      } catch (error) {
        console.error('Tenant operation error:', error);
        const fieldErrors = extractFieldErrors(error);
        if (Object.keys(fieldErrors).length > 0) {
          setServerErrors(fieldErrors);
        }
      }
    },
  });

  const formRef = useRef(form);
  formRef.current = form;

  useEffect(() => {
    if (isOpen && tenant) {
      formRef.current.setFieldValue('name', tenant.name || '');
      formRef.current.setFieldValue('description', tenant.description || '');
      setServerErrors({});
    } else if (isOpen && !tenant) {
      formRef.current.reset();
      setServerErrors({});
    }
  }, [isOpen, tenant]);

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'ویرایش مرکز' : 'افزودن مرکز جدید'}
      icon={<Building2 size={20} />}
      iconVariant="primary"
      size="md"
      closeOnBackdropClick={!isLoading}
      closeOnEscape={!isLoading}
      showCloseButton={!isLoading}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            fullWidth
            onClick={onClose}
            disabled={isLoading}
          >
            انصراف
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={isLoading}
            icon={<Check size={18} />}
            iconPosition="left"
            onClick={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
          >
            {isEditMode ? 'ذخیره تغییرات' : 'افزودن مرکز'}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        className="space-y-5 p-6"
      >
        <form.Field
          name="name"
          validators={{
            onChange: tenantSchema.shape.name,
          }}
        >
          {(field) => {
            const fieldError = field.state.meta.errors?.[0];
            const errorMessage = fieldError
              ? typeof fieldError === 'string'
                ? fieldError
                : fieldError.message || 'خطای اعتبارسنجی'
              : serverErrors.name;

            return (
              <Input
                label="نام مرکز"
                type="text"
                placeholder="نام مرکز را وارد کنید"
                value={field.state.value}
                onChange={(e) => {
                  field.handleChange(e.target.value);
                  if (serverErrors.name) {
                    setServerErrors((prev) => ({
                      ...prev,
                      name: undefined,
                    }));
                  }
                }}
                error={errorMessage}
                icon={<Building2 size={18} />}
                disabled={isLoading}
              />
            );
          }}
        </form.Field>

        <form.Field
          name="description"
          validators={{
            onChange: tenantSchema.shape.description,
          }}
        >
          {(field) => {
            const fieldError = field.state.meta.errors?.[0];
            const errorMessage = fieldError
              ? typeof fieldError === 'string'
                ? fieldError
                : fieldError.message || 'خطای اعتبارسنجی'
              : serverErrors.description;

            return (
              <div>
                <label className="text-tertiary-80 mb-2 block text-sm font-medium dark:text-gray-300">
                  توضیحات
                </label>
                <textarea
                  value={field.state.value}
                  onChange={(e) => {
                    field.handleChange(e.target.value);
                    if (serverErrors.description) {
                      setServerErrors((prev) => ({
                        ...prev,
                        description: undefined,
                      }));
                    }
                  }}
                  placeholder="توضیحات مرکز را وارد کنید"
                  rows={4}
                  disabled={isLoading}
                  className={cn(
                    'w-full resize-none rounded-xl border px-4 py-3 transition-all duration-300 focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
                    // Light & Dark base styles
                    'border-tertiary-20 text-tertiary-100 focus:border-primary-100 focus:ring-primary-20 bg-white',
                    'dark:border-gray-600 dark:bg-gray-700/50 dark:text-gray-200 dark:focus:border-orange-500 dark:focus:ring-orange-500/20',
                    // Error styles overriding base states
                    errorMessage &&
                      'border-red-500 focus:border-red-500 focus:ring-red-500/20 dark:border-red-500 dark:focus:border-red-500 dark:focus:ring-red-500/20',
                  )}
                />
                {errorMessage && (
                  <p className="mt-1.5 flex items-center gap-1 text-xs text-red-500">
                    <Info size={14} />
                    {errorMessage}
                  </p>
                )}
              </div>
            );
          }}
        </form.Field>
      </form>
    </BaseModal>
  );
}

TenantModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  tenant: PropTypes.object,
  isLoading: PropTypes.bool,
};

export default TenantModal;
